/**
 * Single source of truth for property media handling.
 *
 * `Property.image_url` is a mixed array: it can contain photos *and* video
 * URLs. Every consumer (SEO metadata, JSON-LD, cards, galleries, the OG image
 * endpoint) must agree on which entry is which, so the detection lives here
 * instead of being re-implemented per component.
 */

const VIDEO_EXT =
  /\.(mp4|m4v|mov|webm|ogv|ogg|mkv|avi|m3u8|mpd)(?:$|[?#])/i;

/** Pathname of a URL, ignoring query strings and hashes. */
export function urlPath(url: string): string {
  try {
    return new URL(url, "https://placeholder.local").pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

/**
 * True when the URL points at a video. Query strings are ignored so signed or
 * cache-busted video URLs (`clip.mp4?token=…`) are still recognised.
 */
export function isVideoFile(url?: string | null): boolean {
  if (!url) return false;
  return VIDEO_EXT.test(urlPath(url).toLowerCase());
}

/**
 * Conventional poster key for a video: `<name>-poster.jpg`. Generated by the
 * video brander; may 404 for legacy videos, so always treat it as optional.
 */
export function videoPosterUrl(videoUrl: string): string {
  const trimmed = videoUrl.split(/[?#]/)[0];
  const match = trimmed.match(VIDEO_EXT);
  if (!match) return "";
  return `${trimmed.slice(0, match.index)}-poster.jpg`;
}

/** First non-empty, non-video entry in a media list. */
export function firstImage(media?: string[] | null): string | undefined {
  return (media ?? []).find((url) => !!url && !isVideoFile(url));
}

/** First non-empty video entry in a media list. */
export function firstVideo(media?: string[] | null): string | undefined {
  return (media ?? []).find((url) => !!url && isVideoFile(url));
}

/** All non-video entries, preserving gallery order. */
export function imagesOnly(media?: string[] | null): string[] {
  return (media ?? []).filter((url) => !!url && !isVideoFile(url));
}

/**
 * The one media pair that represents a listing everywhere it is shared:
 * social previews, JSON-LD and cards. Never returns a video as `image`.
 */
export function primaryShareMedia(media?: string[] | null): {
  image?: string;
  video?: string;
} {
  return { image: firstImage(media), video: firstVideo(media) };
}

/**
 * Absolute URL for a stored media path, or `undefined` when there is nothing
 * usable. Deliberately does *not* fall back to the site logo: a 74x72 logo
 * fails WhatsApp's 300 px minimum and produces a broken-looking preview.
 */
export function absoluteMediaUrl(
  url: string | undefined | null,
  siteUrl: string,
): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("/")) return `${siteUrl}${url}`;
  return `${siteUrl}/${url}`;
}

/**
 * Best-effort poster for a video, preferring an explicit value (e.g. an API
 * `video_poster_url` field) and falling back to the derived `-poster.jpg` key.
 */
export function posterFor(
  videoUrl: string | undefined,
  explicitPoster?: string | null,
): string | undefined {
  if (explicitPoster) return explicitPoster;
  if (!videoUrl) return undefined;
  return videoPosterUrl(videoUrl) || undefined;
}
